import { Document as RichTextDocument } from '@contentful/rich-text-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useRollbar } from '@rollbar/react';
import { useRouter } from 'next/router';
import { Fragment, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { infer as zodInfer, object as zodObject } from 'zod';

import Button from '@/components/BaseLibrary/Button/Button';
import LeadFormContainer from '@/components/Form/LeadFormContainer/LeadFormContainer';
import { VideoContent } from '@/components/Video/VideoPlayer';
import { Contentful, Footer, FrontifyImageContent } from '@/contentful';
import useDeviceWidth from '@/hooks/deviceWidth';
import {
  LeadFormInputData,
  OwnHome,
  useSubmitLeadToZeus,
} from '@/hooks/useSubmitLeadToZeus';
import useViewPortSize from '@/hooks/useViewportSize';
import { SunrunThemeOptions } from '@/styles/sunrunTheme.styles';
import { CTATypeCF } from '@/types';
import { setImgWidth } from '@/utils/frontify';
import { getErrorMessage, getErrorObject } from '@/utils/getErrorMessage';
import {
  billAmountsValidator,
  emailValidator,
  firstNameValidator,
  InputName,
  lastNameValidator,
  ownHomeValidator,
  phoneValidator,
  referredValidator,
  zipCodeValidator,
} from '@/utils/validation/inputs';

import CallToActionScreen from './CallToActionScreen';
import useStyles from './InteractiveQuoteForm.styles';
import InteractiveQuoteFormScreen from './InteractiveQuoteFormScreen';
import { ProgressStepper } from './ProgressStepper';
import BillRangeStep from './Steps/BillRangeStep';
import EmailStep from './Steps/EmailStep';
import NameStep from './Steps/NameStep';
import OwnHomeStep from './Steps/OwnHomeStep';
import PhoneStep from './Steps/PhoneStep';
import ZipCodeStep from './Steps/ZipCodeStep';

export interface InteractiveQuoteFormContent extends Contentful {
  fields: {
    zipFullScreenImage?: FrontifyImageContent[];
    zipHeading?: string;
    zipSubheading?: string;
    homeOwnershipImage?: FrontifyImageContent[];
    homeOwnershipHeading?: string;
    electricBillImage?: FrontifyImageContent[];
    electricBillHeading?: string;
    emailImage?: FrontifyImageContent[];
    emailHeading?: string;
    emailSubheading?: string;
    nameImage?: FrontifyImageContent[];
    nameHeading?: string;
    nameSubheading?: string;
    phoneImage?: FrontifyImageContent[];
    phoneHeading?: string;
    tcpa: RichTextDocument;
    notHomeownerHeading?: string;
    notHomeownerSubheading?: string;
    notHomeownerVideos?: VideoContent[];
    confirmationHeading?: string;
    confirmationSubheading?: string;
    confirmationCta?: CTATypeCF;
    confirmationProgressSteps?: string[];
    confirmationLastCompletedStepNumber: number;
    confirmationVideos?: VideoContent[];
    confirmationFullScreenImage?: FrontifyImageContent[];
    footer?: Footer;
    campaignId?: string;
    overrideCampaignId: boolean;
  };
}

interface FormScreenData {
  heading?: string;
  subheading?: string;
  rightHalfImage?: string;
  fullScreenImage?: string;
  formContent?: React.JSX.Element;
  progressBarPercentage?: number;
}

export interface CallToActionScreenData {
  fullScreenImage?: string;
  heading?: string;
  subheading?: string;
  formContent?: React.JSX.Element;
  videos?: VideoContent[];
  footer?: Footer;
}

export interface Props {
  content: InteractiveQuoteFormContent;
}

const quoteFormSchema = zodObject({
  ...zipCodeValidator,
  ...ownHomeValidator,
  ...billAmountsValidator,
  ...emailValidator,
  ...firstNameValidator,
  ...lastNameValidator,
  ...phoneValidator,
  ...referredValidator,
});

export type QuoteFormSchemaType = zodInfer<typeof quoteFormSchema>;

export default function InteractiveQuoteForm({ content }: Props) {
  const [currentStepIndex, setCurrentStepIndex] = useState<
    1 | 2 | 3 | 4 | 5 | 6 | 7 | 8
  >(1);
  const [networkError, setNetworkError] = useState<string>('');

  const { classes } = useStyles();
  const { isTablet } = useDeviceWidth();
  const { isExtraSmall, isSmall } = useViewPortSize();
  const isMobile = isExtraSmall || isSmall;
  const router = useRouter();
  const { submitLeadToZeus } = useSubmitLeadToZeus();
  const rollbar = useRollbar();

  const {
    zipFullScreenImage,
    zipHeading,
    zipSubheading,
    homeOwnershipImage,
    homeOwnershipHeading,
    electricBillImage,
    electricBillHeading,
    emailImage,
    emailHeading,
    emailSubheading,
    nameImage,
    nameHeading,
    nameSubheading,
    phoneImage,
    phoneHeading,
    tcpa,
    notHomeownerHeading,
    notHomeownerSubheading,
    notHomeownerVideos,
    confirmationHeading,
    confirmationSubheading,
    confirmationCta,
    confirmationProgressSteps = [],
    confirmationLastCompletedStepNumber,
    confirmationVideos,
    confirmationFullScreenImage,
    footer,
    campaignId,
    overrideCampaignId = false,
  } = content?.fields || {};

  if (!currentStepIndex) {
    return;
  }
  const { handleSubmit, setValue } = useForm<QuoteFormSchemaType>({
    resolver: zodResolver(quoteFormSchema),
    mode: 'onTouched',
  });

  const onSubmit: SubmitHandler<QuoteFormSchemaType> = async (
    data: QuoteFormSchemaType
  ) => {
    const zeusData: LeadFormInputData = {
      first_name: data.firstName,
      last_name: data.lastName,
      email: data.email,
      zip: data.zipCode,
      phone_home: data.phone,
      property_ownership: data.ownHome,
      electric_bill: data.lastBill,
      Referral_web_form__c: data.referred ? 'yes' : 'no',
    };
    try {
      await submitLeadToZeus({
        zeusData,
        contentfulCampaignId: campaignId,
        gtmSubmitEventFormType: 'Interactive Quote Form',
        overrideCampaignId,
      });
      setNetworkError('');
    } catch (err: unknown) {
      rollbar.error(getErrorMessage(err), getErrorObject(err));
      setNetworkError(
        'There was an error submitting the form, please try again'
      );
    }
    setCurrentStepIndex(7);
  };

  const imageIndex = isMobile ? 2 : isTablet ? 1 : 0;
  const rightHalfImageMaxWidth = isMobile ? 768 : isTablet ? 1060 : 1440;

  const zipStep: FormScreenData = {
    formContent: (
      <ZipCodeStep
        handleNext={(data) => {
          if (currentStepIndex === 1) {
            setCurrentStepIndex(2);
            setValue(InputName.ZIP_CODE, data.zipCode);
          }
        }}
      />
    ),
    fullScreenImage: setImgWidth(
      zipFullScreenImage?.[imageIndex]?.preview_url,
      4320
    ),
    heading: zipHeading,
    subheading: zipSubheading,
  };

  const homeOwnershipScreen: FormScreenData = {
    progressBarPercentage: 20,
    formContent: (
      <OwnHomeStep
        handleNext={(data) => {
          const stepIndex = data === OwnHome.OWN ? 3 : 8;
          setCurrentStepIndex(stepIndex);
          setValue(InputName.OWN_HOME, data);
        }}
      />
    ),
    rightHalfImage: setImgWidth(
      homeOwnershipImage?.[imageIndex]?.preview_url || '',
      rightHalfImageMaxWidth
    ),
    heading: homeOwnershipHeading,
  };

  const electricBillScreen: FormScreenData = {
    progressBarPercentage: 40,
    formContent: (
      <BillRangeStep
        handleNext={(data) => {
          setCurrentStepIndex(4);
          setValue(InputName.LAST_BILL, data);
        }}
      />
    ),
    rightHalfImage: setImgWidth(
      electricBillImage?.[imageIndex]?.preview_url || '',
      rightHalfImageMaxWidth
    ),
    heading: electricBillHeading,
  };

  const emailScreen: FormScreenData = {
    progressBarPercentage: 60,
    formContent: (
      <EmailStep
        handleNext={(data) => {
          setCurrentStepIndex(5);
          setValue(InputName.EMAIL, data.email);
        }}
      />
    ),
    rightHalfImage: setImgWidth(
      emailImage?.[imageIndex]?.preview_url || '',
      rightHalfImageMaxWidth
    ),
    heading: emailHeading,
    subheading: emailSubheading,
  };

  const nameScreen: FormScreenData = {
    progressBarPercentage: 80,
    formContent: (
      <NameStep
        handleNext={(data) => {
          setCurrentStepIndex(6);
          setValue(InputName.FIRST_NAME, data.firstName);
          setValue(InputName.LAST_NAME, data.lastName);
        }}
      />
    ),
    rightHalfImage: setImgWidth(
      nameImage?.[imageIndex]?.preview_url || '',
      rightHalfImageMaxWidth
    ),
    heading: nameHeading,
    subheading: nameSubheading,
  };

  const phoneScreen: FormScreenData = {
    progressBarPercentage: 95,
    formContent: (
      <PhoneStep
        handleNext={(data) => {
          setValue(InputName.PHONE, data.phone);
          setValue(InputName.REFERRED, data.referred);
          handleSubmit(onSubmit)();
        }}
        networkError={networkError}
        tcpa={tcpa}
      />
    ),
    rightHalfImage: setImgWidth(
      phoneImage?.[imageIndex]?.preview_url || '',
      rightHalfImageMaxWidth
    ),
    heading: phoneHeading,
  };

  const confirmationScreen: CallToActionScreenData = {
    heading: confirmationHeading,
    subheading: confirmationSubheading,
    formContent: (
      <Fragment>
        {confirmationCta && (
          <Button
            label={confirmationCta.fields.text}
            href={confirmationCta.fields.url}
            className={classes.confirmationCTA}
          />
        )}
        <ProgressStepper
          steps={confirmationProgressSteps}
          lastCompletedStep={confirmationLastCompletedStepNumber}
        />
      </Fragment>
    ),
    videos: confirmationVideos,
    fullScreenImage: setImgWidth(
      confirmationFullScreenImage?.[imageIndex]?.preview_url,
      4320
    ),
    footer,
  };

  const notHomeOwnerScreen: CallToActionScreenData = {
    heading: notHomeownerHeading,
    subheading: notHomeownerSubheading,
    videos: notHomeownerVideos,
    formContent: (
      <Button
        onClick={() => {
          router.push('/referral');
        }}
        label='Refer a Friend'
        className={classes.button}
      />
    ),
    footer,
  };

  const steps: { [index: number]: FormScreenData | CallToActionScreenData } = {
    1: zipStep,
    2: homeOwnershipScreen,
    3: electricBillScreen,
    4: emailScreen,
    5: nameScreen,
    6: phoneScreen,
    7: confirmationScreen,
    8: notHomeOwnerScreen,
  };

  if ([1, 2, 3, 4, 5, 6].includes(currentStepIndex)) {
    const { fullScreenImage } = steps[currentStepIndex] as FormScreenData;
    if (footer)
      footer.fields.textColor = fullScreenImage
        ? SunrunThemeOptions.brandColors.white
        : SunrunThemeOptions.brandColors.primaryMain;
    return (
      <LeadFormContainer
        handleKeyDown={(e) => {
          if (e.key === 'Enter') {
            // Keeps form from submiting on hitting enter
            e.preventDefault();
          }
        }}
      >
        {Object.keys(steps).map((step) => (
          <InteractiveQuoteFormScreen
            key={step}
            stepIndex={Number(step)}
            currentStepIndex={currentStepIndex}
            {...steps[Number(step)]}
          />
        ))}
      </LeadFormContainer>
    );
  } else {
    return <CallToActionScreen data={steps[currentStepIndex]} />;
  }
}
